import React, { useState, useEffect, useMemo } from 'react'
import dependencyManagerContext from '../dependencyManagerContext'
import resources from '@/assets/locale'
import { DirectionProvider, ThemeProvider as VeneerThemeProvider } from '@veneer/core'
import projectNames from '../configs/projectNames'
import getCommonsNorthboundAPIs from '../utils/getCommonsNorthboundAPIs'
import * as T from '../types'
import {
  useMultiUiBrandContext,
  getThemeProviderConfig,
} from '@jarvis/react-setup-and-onboarding-addons'
import { ThemeProvider } from '@/providers/ThemeProvider'
import { setUi } from '@/stores/config'
import { MfeSection } from './DependencyManagerProvider.styled'

/**
 * This component is responsible to manage global dependencies and provide them to the children components
 * It should be used by the main component of your project
 * You can use the hook to get the dependencies in any child component
 * It is configured by default to be used inside MFE environment, but you can change it to be used in any other environment
 */
const DependencyManagerProvider: React.FC<
  T.DependencyManagerProviderPropsType
> = ({ bundleName, children }) => {
  const { uiBrand } = useMultiUiBrandContext()

  const [globalTranslate, setGlobalTranslate] =
    useState<T.DependencyManagerContextType['globalTranslate']>()
  const [translate, setTranslate] = useState<T.TranslateType | undefined>()

  const commonsNorthboundAPIs = React.useMemo(
    () => getCommonsNorthboundAPIs(),
    [],
  )

  const directionValue =
    commonsNorthboundAPIs?.v1?.localization?.useReactGetLanguageDirection?.(
      React,
    )

  const { themeProviderProps, userThemeMode } = useMemo(() => {
    return {
      themeProviderProps:
        commonsNorthboundAPIs?.v1?.theme?.getThemeProviderProperties?.(),
      userThemeMode: commonsNorthboundAPIs?.v1?.theme?.getUserThemeMode?.(),
    }
  }, [commonsNorthboundAPIs?.v1?.theme])

  useEffect(() => {
    setUi(uiBrand)
  }, [uiBrand])

  // Create a translator function by providing the resource files
  useEffect(() => {
    const options = {
      fallbackLng: ['en_US'],
      ensureFallbackLng: true,
    }
    commonsNorthboundAPIs.v1.localization
      .createTranslatorFunction(resources, options)
      .then((response) => setTranslate(() => response))
  }, [commonsNorthboundAPIs.v1.localization])

  useEffect(() => {
    commonsNorthboundAPIs.v1.localization
      ?.getGlobalTranslatorFunction?.()
      ?.then((response) => setGlobalTranslate(() => response))
  }, [commonsNorthboundAPIs.v1.localization])

  /**
   * It will block rendering until translate function is available
   * You can remove this if you want to render the children without waiting for translate function
   * But you should check if translate exists before using it
   */
  if (!translate || !globalTranslate) {
    return null
  }

  return (
    <MfeSection
      className={`${projectNames.sanitizedPackageName}`}
      id={`${projectNames.packageName}_${bundleName}`}
    >
      <DirectionProvider direction={directionValue}>
        <VeneerThemeProvider
          {...getThemeProviderConfig(uiBrand)}
          {...themeProviderProps}
          mode={userThemeMode}
        >
          <dependencyManagerContext.Provider
            value={{
              globalTranslate,
              translate,
              northboundAPIs: commonsNorthboundAPIs,
            }}
          >
            <ThemeProvider>
              {children}
            </ThemeProvider>
          </dependencyManagerContext.Provider>
        </VeneerThemeProvider>
      </DirectionProvider>
    </MfeSection>
  )
}

export default DependencyManagerProvider
